import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useMutation, useQuery } from '@apollo/client'
import { GET_UI_SETTINGS, UPDATE_UI_SETTINGS } from './uiSettingsQueries'

interface Setting {
  name: string
  title: string
  type: string
  value: string
  shopSettingId: number
  description: string
  __typename: string
}

export default function UISettings() {
  const { data, loading, refetch } = useQuery(GET_UI_SETTINGS)
  const [updateShopSettings] = useMutation(UPDATE_UI_SETTINGS)

  const [settings, setSettings] = useState<Setting[]>([])
  const [savingSettings, setSavingSettings] = useState<Set<string>>(new Set())

  // Initialize settings when data is loaded
  useEffect(() => {
    if (data?.findManySetting) {
      const settingsData = data.findManySetting as Setting[]
      setSettings(settingsData)
    }
  }, [data])

  const handleSettingToggle = async (
    settingName: string,
    newValue: boolean
  ) => {
    // Update local state immediately for responsive UI
    setSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.name === settingName
          ? { ...setting, value: newValue.toString() }
          : setting
      )
    )

    // Add to saving state
    setSavingSettings(prev => new Set(prev).add(settingName))

    try {
      // Create the update data structure for this specific setting
      const updateData = {
        settings: {
          update: [
            {
              data: {
                value: {
                  set: newValue.toString(),
                },
              },
              where: {
                name: settingName,
              },
            },
          ],
        },
      }

      // Use the shopSettingId from the settings
      const shopSettingId =
        settings.find(s => s.name === settingName)?.shopSettingId || 1

      await updateShopSettings({
        variables: {
          data: updateData,
          where: {
            id: shopSettingId,
          },
        },
      })

      toast.success(
        `"${
          settings.find(s => s.name === settingName)?.title
        }" updated successfully`
      )
    } catch (error) {
      console.error(`Failed to update setting ${settingName}:`, error)
      toast.error(
        `Failed to update "${
          settings.find(s => s.name === settingName)?.title
        }"`
      )

      // Revert the local state on error
      setSettings(prevSettings =>
        prevSettings.map(setting =>
          setting.name === settingName
            ? { ...setting, value: (!newValue).toString() }
            : setting
        )
      )
    } finally {
      // Remove from saving state
      setSavingSettings(prev => {
        const newSet = new Set(prev)
        newSet.delete(settingName)
        return newSet
      })
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex justify-center items-center h-64">
          <div>Loading...</div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center mb-8">
        <HeaderSection
          title="UI Settings"
          subtitle=" Configure various UI features and settings for your application."
        />
      </div>

      <div className="max-w-[1000px]">
        <Card className="p-6">
          <div className="space-y-6">
            {settings.map(setting => (
              <div
                key={setting.name}
                className="flex items-start justify-between py-4 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex-1">
                  <h3 className="text-base font-semibold mb-1">
                    {setting.title}
                  </h3>
                  {setting.description && (
                    <p className="text-sm text-gray-600 mb-2">
                      {setting.description}
                    </p>
                  )}
                  <div className="text-xs text-gray-500">{setting.name}</div>
                </div>

                <div className="ml-6">
                  <Switch
                    checked={setting.value === 'true'}
                    onCheckedChange={checked =>
                      handleSettingToggle(setting.name, checked)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {settings.length === 0 && (
          <Card className="p-6">
            <div className="text-center text-gray-500">
              No UI settings found.
            </div>
          </Card>
        )}
      </div>
    </PageContent>
  )
}
